import React, { useState, useEffect } from 'react';
import { X, Save, Calendar } from 'lucide-react';
import Select from 'react-select';
import api from '../api';

const NotificationForm = ({ notification, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    titre: '',
    message: '',
    type_notification: 'INFO',
    priorite: 'NORMALE',
    type_cible: 'TOUS',
    utilisateurs_cibles_ids: [],
    groupe_cible_id: null,
    est_programmee: false,
    date_envoi_programmee: '',
    envoyer_email: false,
    envoyer_push: true,
    est_importante: false,
    lien: ''
  });

  const [utilisateurs, setUtilisateurs] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (notification) {
      setFormData({
        titre: notification.titre || '',
        message: notification.message || '',
        type_notification: notification.type_notification || 'INFO',
        priorite: notification.priorite || 'NORMALE',
        type_cible: notification.type_cible || 'TOUS',
        utilisateurs_cibles_ids: notification.utilisateurs_cibles?.map(u => u.id) || [],
        groupe_cible_id: notification.groupe_cible?.id || null,
        est_programmee: notification.est_programmee || false,
        date_envoi_programmee: notification.date_envoi_programmee || '',
        envoyer_email: notification.envoyer_email || false,
        envoyer_push: notification.envoyer_push !== undefined ? notification.envoyer_push : true,
        est_importante: notification.est_importante || false,
        lien: notification.lien || ''
      });
    }
  }, [notification]);

  useEffect(() => {
    // Charger les utilisateurs si c'est une notification ciblée
    if (formData.type_cible === 'CIBLÉ') {
      fetchUtilisateurs();
    }
    // Charger les groupes si c'est une notification de groupe
    if (formData.type_cible === 'GROUPE') {
      fetchGroupes();
    }
  }, [formData.type_cible]);

  const fetchUtilisateurs = async () => {
    try {
      const response = await api.get('/api/utilisateur/admin/utilisateurs/');
      console.log('Utilisateurs chargés:', response.data);
      // L'API retourne { results, count, total_pages, ... }
      const users = response.data.results || response.data;
      setUtilisateurs(users);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const fetchGroupes = async () => {
    try {
      const response = await api.get('/api/utilisateur/admin/groupes/');
      console.log('Groupes chargés:', response.data);
      // L'API peut retourner { results, count, ... } ou un tableau direct
      const groups = response.data.results || response.data;
      setGroupes(groups);
    } catch (error) {
      console.error('Erreur lors du chargement des groupes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Nettoyer les données avant l'envoi
      const cleanData = { ...formData };
      
      // Convertir les strings vides en null
      if (cleanData.date_envoi_programmee === '') {
        cleanData.date_envoi_programmee = null;
      }
      if (cleanData.lien === '') {
        cleanData.lien = null;
      }
      
      // Supprimer groupe_cible_id si type_cible n'est pas GROUPE
      if (cleanData.type_cible !== 'GROUPE') {
        cleanData.groupe_cible_id = null;
      }
      
      // Supprimer utilisateurs_cibles_ids si type_cible n'est pas CIBLÉ
      if (cleanData.type_cible !== 'CIBLÉ') {
        cleanData.utilisateurs_cibles_ids = [];
      }
      
      await onSave(cleanData);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      console.error('Détails erreur:', error.response?.data);
      // L'erreur est déjà affichée par la page parente avec toast
    } finally {
      setLoading(false);
    }
  };

  // Options pour react-select (utilisateurs)
  const userOptions = utilisateurs.map(user => ({
    value: user.id,
    label: `${user.first_name} ${user.last_name} (@${user.username})`,
    username: user.username,
    email: user.email
  }));

  // Options pour react-select (groupes)
  const groupeOptions = groupes.map(groupe => ({
    value: groupe.id,
    label: groupe.titre || groupe.nom,
    membres_count: groupe.membres_count
  }));

  // Valeurs sélectionnées pour react-select
  const selectedUsers = userOptions.filter(option => 
    formData.utilisateurs_cibles_ids.includes(option.value)
  );

  const selectedGroupe = groupeOptions.find(option => 
    option.value === formData.groupe_cible_id
  );

  // Custom styles pour react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      borderColor: state.isFocused ? '#FF6B00' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(255, 107, 0, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#FF6B00'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#FF6B00' : state.isFocused ? '#FFF7ED' : 'white',
      color: state.isSelected ? 'white' : '#111827',
      '&:hover': {
        backgroundColor: state.isSelected ? '#FF6B00' : '#FFF7ED'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#FFF7ED',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#FF6B00',
      fontWeight: '500'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#FF6B00',
      '&:hover': {
        backgroundColor: '#FF6B00',
        color: 'white',
      },
    }),
  };

  // Helper pour obtenir le label du type
  const getTypeLabel = (type) => {
    const types = {
      'INFO': '📢 Information',
      'AVERTISSEMENT': '⚠️ Avertissement',
      'URGENCE': '🚨 Urgence',
      'FELICITATION': '🎉 Félicitation',
      'RAPPEL': '⏰ Rappel',
      'EVENEMENT': '📅 Événement'
    };
    return types[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col my-8">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {notification ? 'Modifier la notification' : 'Nouvelle notification'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.titre}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
              placeholder="Ex: Maintenance programmée"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              rows={5}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
              placeholder="Saisissez le message de la notification..."
            />
            <p className="text-sm text-gray-500 mt-1">
              {formData.message.length} caractères
            </p>
          </div>

          {/* Type et Priorité */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de notification
              </label>
              <Select
                options={[
                  { value: 'INFO', label: '📢 Information' },
                  { value: 'AVERTISSEMENT', label: '⚠️ Avertissement' },
                  { value: 'URGENCE', label: '🚨 Urgence' },
                  { value: 'FELICITATION', label: '🎉 Félicitation' },
                  { value: 'RAPPEL', label: '⏰ Rappel' },
                  { value: 'EVENEMENT', label: '📅 Événement' }
                ]}
                value={{ value: formData.type_notification, label: getTypeLabel(formData.type_notification) }}
                onChange={(selected) => setFormData({ ...formData, type_notification: selected.value })}
                styles={customSelectStyles}
                isSearchable={false}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priorité
              </label>
              <Select
                options={[
                  { value: 'BASSE', label: 'Basse' },
                  { value: 'NORMALE', label: 'Normale' },
                  { value: 'HAUTE', label: 'Haute' },
                  { value: 'CRITIQUE', label: 'Critique' }
                ]}
                value={{ value: formData.priorite, label: formData.priorite.charAt(0) + formData.priorite.slice(1).toLowerCase() }}
                onChange={(selected) => setFormData({ ...formData, priorite: selected.value })}
                styles={customSelectStyles}
                isSearchable={false}
              />
            </div>
          </div>

          {/* Type de cible */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destinataires
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type_cible: 'TOUS', utilisateurs_cibles_ids: [], groupe_cible_id: null })}
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg transition-colors ${
                  formData.type_cible === 'TOUS'
                    ? 'border-[#FF6B00] bg-orange-50 text-[#FF6B00]'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                👥 Tous
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, type_cible: 'CIBLÉ', groupe_cible_id: null })}
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg transition-colors ${
                  formData.type_cible === 'CIBLÉ'
                    ? 'border-[#FF6B00] bg-orange-50 text-[#FF6B00]'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                ✅ Ciblés
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, type_cible: 'GROUPE', utilisateurs_cibles_ids: [] })}
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg transition-colors ${
                  formData.type_cible === 'GROUPE'
                    ? 'border-[#FF6B00] bg-orange-50 text-[#FF6B00]'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                👨‍👩‍👧‍👦 Groupe
              </button>
            </div>
          </div>

          {/* Sélection des utilisateurs ciblés */}
          {formData.type_cible === 'CIBLÉ' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner les utilisateurs <span className="text-red-500">*</span>
              </label>
              <Select
                isMulti
                options={userOptions}
                value={selectedUsers}
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    utilisateurs_cibles_ids: selected ? selected.map(opt => opt.value) : []
                  });
                }}
                styles={customSelectStyles}
                placeholder="Rechercher et sélectionner des utilisateurs..."
                noOptionsMessage={() => "Aucun utilisateur trouvé"}
                loadingMessage={() => "Chargement..."}
                className="basic-multi-select"
                classNamePrefix="select"
              />
              <p className="text-sm text-gray-500 mt-2">
                {formData.utilisateurs_cibles_ids.length} utilisateur(s) sélectionné(s)
              </p>
            </div>
          )}

          {/* Sélection du groupe */}
          {formData.type_cible === 'GROUPE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un groupe <span className="text-red-500">*</span>
              </label>
              <Select
                options={groupeOptions}
                value={selectedGroupe}
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    groupe_cible_id: selected ? selected.value : null
                  });
                }}
                styles={customSelectStyles}
                placeholder="Choisir un groupe..."
                noOptionsMessage={() => "Aucun groupe trouvé"}
                loadingMessage={() => "Chargement..."}
                isClearable
                formatOptionLabel={(option) => (
                  <div className="flex items-center justify-between">
                    <span>{option.label}</span>
                    {option.membres_count !== undefined && (
                      <span className="text-sm text-gray-500">
                        {option.membres_count} membre(s)
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Programmation */}
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={formData.est_programmee}
                onChange={(e) => setFormData({ ...formData, est_programmee: e.target.checked })}
                className="w-4 h-4 text-[#FF6B00] focus:ring-[#FF6B00]"
              />
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                <span className="font-medium">Programmer l'envoi</span>
              </div>
            </label>

            {formData.est_programmee && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date et heure d'envoi
                </label>
                <input
                  type="datetime-local"
                  value={formData.date_envoi_programmee}
                  onChange={(e) => setFormData({ ...formData, date_envoi_programmee: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Options d'envoi</h3>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.envoyer_push}
                onChange={(e) => setFormData({ ...formData, envoyer_push: e.target.checked })}
                className="w-4 h-4 text-[#FF6B00] focus:ring-[#FF6B00]"
              />
              <span>Envoyer une notification push</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.envoyer_email}
                onChange={(e) => setFormData({ ...formData, envoyer_email: e.target.checked })}
                className="w-4 h-4 text-[#FF6B00] focus:ring-[#FF6B00]"
              />
              <span>Envoyer également par email</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.est_importante}
                onChange={(e) => setFormData({ ...formData, est_importante: e.target.checked })}
                className="w-4 h-4 text-[#FF6B00] focus:ring-[#FF6B00]"
              />
              <span>Marquer comme importante</span>
            </label>
          </div>

          {/* Lien optionnel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lien (optionnel)
            </label>
            <input
              type="url"
              value={formData.lien}
              onChange={(e) => setFormData({ ...formData, lien: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
              placeholder="https://..."
            />
          </div>
        </form>

        {/* Actions */}
        <div className="border-t p-6 bg-gray-50 sticky bottom-0">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors font-medium disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationForm;
